import React, {
  Dispatch, SetStateAction, useEffect, useRef, useState, Ref,
} from 'react';
import {
  Animated, View, StyleSheet, Text, TouchableOpacity, ActivityIndicator, ScrollView, Pressable,
  Modal,
} from 'react-native';
import { useSelector } from 'react-redux';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import FilterSearchBox from '../FilterSearchBox';
import Colors from '../../constants/Colors';
import { Area, Floor, Space } from '../Map/SetFloorsModal/FloorItem';
import FloorDropdown from '../../screens/Tickets/components/TicketsFilter/components/FloorDropdown';
import AreaDropdown from '../../screens/Tickets/components/TicketsFilter/components/AreaDropdown';
import SpaceDropdown from '../../screens/Tickets/components/TicketsFilter/components/SpaceDropdown';
import Button from '../Button';
import isMobile from '../IsMobile';
import Style from '../../constants/Style';
import getAssets from '../../requests/Assets/GetAssets';
import AlertModal from '../AlertModal';
import ResetFiltersButton from '../ResetFiltersButton';

interface AssetFilterProps {
  clearAssetFilter: () => void,
  logEvent,
  assetFilterLoading: boolean,
  setAssetFilterLoading: Dispatch<SetStateAction<boolean>>,
  assetFilterResults,
  setAssetFilterResults,
  detailedFilterVisible: boolean,
  setDetailedFilterVisible: Dispatch<SetStateAction<boolean>>,
}

const AssetFilter = ({
  clearAssetFilter, assetFilterLoading, setAssetFilterLoading, assetFilterResults, logEvent,
  detailedFilterVisible, setDetailedFilterVisible, setAssetFilterResults,
}: AssetFilterProps) => {
  const buildings: object = useSelector((state:
    { locations: { buildings: object } }) => state.locations.buildings);
  const selectedBuildingId: string = useSelector((state:
    { selectedBuilding: { id: string } }) => state.selectedBuilding.id);
  const [name, setName] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [manufacturer, setManufacturer] = useState<string>('');
  const [model, setModel] = useState<string>('');
  const [serial, setSerial] = useState<string>('');
  const [selectedFloor, setSelectedFloor] = useState<Floor>(null);
  const [selectedArea, setSelectedArea] = useState<Area>(null);
  const [selectedSpace, setSelectedSpace] = useState<Space>(null);
  const [floorDropdownShown, setFloorDropdownShown] = useState<boolean>(false);
  const [areaDropdownShown, setAreaDropdownShown] = useState<boolean>(false);
  const [spaceDropdownShown, setSpaceDropdownShown] = useState<boolean>(false);
  const [selectedLocationId, setSelectedLocationId] = useState<string>(selectedBuildingId);
  const { top, bottom, right } = useSafeAreaInsets();
  const [translateX] = useState(new Animated.Value(300));
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const scrollView: Ref<ScrollView> = useRef<ScrollView>(null);
  const filterSnapshot = useRef<{
    name: string,
    description: string,
    manufacturer: string,
    model: string,
    serial: string,
    selectedFloor: Floor,
    selectedArea: Area,
    selectedSpace: Space,
    selectedLocationId: string,
  }>({
    name,
    description,
    manufacturer,
    model,
    serial,
    selectedFloor,
    selectedArea,
    selectedSpace,
    selectedLocationId,
  });

  const closeLocationDropdowns: () => void = () => {
    setFloorDropdownShown(false);
    setAreaDropdownShown(false);
    setSpaceDropdownShown(false);
  };

  const revertFilterValues: () => void = () => {
    setName(filterSnapshot.current.name);
    setDescription(filterSnapshot.current.description);
    setManufacturer(filterSnapshot.current.manufacturer);
    setModel(filterSnapshot.current.model);
    setSerial(filterSnapshot.current.serial);
    setSelectedFloor(filterSnapshot.current.selectedFloor);
    setSelectedArea(filterSnapshot.current.selectedArea);
    setSelectedSpace(filterSnapshot.current.selectedSpace);
    setSelectedLocationId(filterSnapshot.current.selectedLocationId);
  };

  useEffect(() => {
    if (detailedFilterVisible) {
      filterSnapshot.current = {
        name,
        description,
        manufacturer,
        model,
        serial,
        selectedFloor,
        selectedArea,
        selectedSpace,
        selectedLocationId,
      };
      Animated.timing(translateX, {
        toValue: 0,
        duration: 200,
        useNativeDriver: true,
      }).start();
    } else {
      Animated.timing(translateX, {
        toValue: 300,
        duration: 200,
        useNativeDriver: true,
      }).start();
    }
  }, [detailedFilterVisible]);

  useEffect(() => {
    scrollView?.current?.scrollToEnd();
  }, [floorDropdownShown, areaDropdownShown, spaceDropdownShown]);

  const reset: () => void = () => {
    logEvent('AssetManagementWeb', 'Reset_All_Pressed');
    setName('');
    setDescription('');
    setManufacturer('');
    setModel('');
    setDescription('');
    setSelectedFloor(null);
    setSelectedArea(null);
    setSelectedSpace(null);
    closeLocationDropdowns();
  };

  const apply: () => void = () => {
    setAssetFilterLoading(true);
    getAssets({
      name,
      description,
      manufacturer,
      model,
      serial,
      locationId: selectedLocationId || selectedBuildingId,
      enableSubLocationsLookUp: true,
    }).then((response) => {
      if (response?.assets?.length > 0) {
        setAssetFilterResults(response.assets);
        setDetailedFilterVisible(false);
      } else setErrorMessage('No assets found with the provided filter values');
      setAssetFilterLoading(false);
    }).catch((error: { message: string }) => {
      setErrorMessage(error?.message);
      setAssetFilterLoading(false);
    });
  };

  const cancel: () => void = () => {
    closeLocationDropdowns();
    revertFilterValues();
    setDetailedFilterVisible(false);
    setAssetFilterLoading(false);
  };

  const Filters = () => <View style={{ flex: 1 }}>
    <Text style={styles.selectedTitle}>{'Filter'}</Text>
    <ScrollView
      style={{ flexGrow: 1 }}
      contentContainerStyle={styles.content}
      keyboardShouldPersistTaps='handled'
      ref={scrollView}
    >
      <FilterSearchBox
        title={'Name'}
        value={name}
        onChangeText={setName}
      />
      <FilterSearchBox
        title={'Model'}
        value={model}
        onChangeText={setModel}
      />
      <FilterSearchBox
        title={'Serial number'}
        value={serial}
        onChangeText={setSerial}
      />
      <FilterSearchBox
        title={'Description'}
        value={description}
        onChangeText={setDescription}
      />
      <FilterSearchBox
        title={'Manufacturer'}
        value={manufacturer}
        onChangeText={setManufacturer}
      />
      <View style={styles.locationDropdownSection}>
        <FloorDropdown
          {...{
            selectedFloor,
            selectedLocationId,
            setFloorDropdownShown,
            setSelectedFloor,
            setSelectedArea,
            setSelectedSpace,
            floorDropdownShown,
            setSelectedLocationId,
          }}
          closeOtherDropdowns={() => {
            setAreaDropdownShown(false);
            setSpaceDropdownShown(false);
          }}
          locations={buildings ? Object.values(buildings[selectedBuildingId]?.floors ?? {}) ?? []
            : []}
        />
        {Object.values(selectedFloor?.areas ?? {})?.length > 0 && <AreaDropdown
          {...{
            selectedArea,
            setFloorDropdownShown,
            setAreaDropdownShown,
            setSpaceDropdownShown,
            setSelectedArea,
            setSelectedSpace,
            areaDropdownShown,
            setSelectedLocationId,
          }}
          closeOtherDropdowns={() => {
            setFloorDropdownShown(false);
            setSpaceDropdownShown(false);
          }}
          floor={selectedFloor}
        />}
        {Object.values(selectedArea?.spaces ?? {}).length > 0 && <SpaceDropdown
          {...{
            selectedSpace,
            setFloorDropdownShown,
            setAreaDropdownShown,
            setSpaceDropdownShown,
            setSelectedSpace,
            setSelectedLocationId,
            spaceDropdownShown,
          }}
          closeOtherDropdowns={() => {
            setFloorDropdownShown(false);
            setAreaDropdownShown(false);
          }}
          area={selectedArea}
        />}
      </View>
    </ScrollView>
    <View style={styles.content}>
      <ResetFiltersButton onPress={reset} />
      {assetFilterResults.length > 0 && (
        <TouchableOpacity
          style={styles.resetAllFiltersButton}
          onPress={() => {
            logEvent('AssetManagementWeb', 'Clear_Search_Results_pressed');
            reset();
            clearAssetFilter();
          }}
        >
          <Text style={styles.resetAllFiltersText}>{'CLEAR SEARCH RESULTS'}</Text>
        </TouchableOpacity>
      )}
      <View style={styles.assetFilterBottomSection}>
        <Button
          affirmative
          disabled={(manufacturer.trim().length === 0 && description.trim().length === 0
            && serial.trim().length === 0 && name.trim().length === 0 && model.trim().length === 0
            && !selectedLocationId) || assetFilterLoading}
          title='Apply'
          onPress={apply}
          style={styles.applyButton}
          testID='apply-filter-asset-btn'
        />
        <Button
          title='Cancel'
          onPress={cancel}
          style={styles.cancelButton}
        />
        {assetFilterLoading && <ActivityIndicator size="large" />}
      </View>
    </View>
    <AlertModal
      title='Error'
      message={errorMessage}
      modalVisible={!!errorMessage}
      setModalVisible={() => setErrorMessage(null)}
    />
  </View>;

  return isMobile ? <Modal
    style={Style.modal}
    visible={detailedFilterVisible}
    onRequestClose={cancel}
    transparent={true}
    animationType='fade'
  >
    <View style={{ flex: 1, flexDirection: 'row', backgroundColor: 'rgba(0, 0, 0, 0.5)' }}>
      <Pressable onPress={cancel} style={{ flex: 1, backgroundColor: '#00000010' }} />
      <View
        style={[
          styles.containerMobile,
          { paddingTop: top > 0 ? top : 24 },
          { paddingBottom: bottom > 0 ? 4 + bottom : 24 },
          { width: 300 + right },
        ]}
      >
        {Filters()}
      </View>
    </View>
  </Modal> : <Pressable>
    <Animated.View style={[styles.container, { transform: [{ translateX }] }]}>
      {Filters()}
    </Animated.View>
  </Pressable>;
};

const styles = StyleSheet.create({
  container: {
    width: 300,
    position: 'absolute',
    height: '100%',
    zIndex: 5,
    right: 0,
    backgroundColor: '#fff',
    borderLeftWidth: 1,
    borderColor: Colors.light.hereworksBlue100,
    paddingBottom: 24,
  },
  content: {
    paddingHorizontal: 24,
    flexGrow: 1,
  },
  selectedTitle: {
    fontFamily: 'Poppins_400Regular',
    fontSize: 18,
    lineHeight: 27,
    color: Colors.light.hereworksBlack600,
    margin: 24,
  },
  filterButton: {
    flexDirection: 'row',
    alignItems: 'center',
    borderRadius: 4,
    borderWidth: 1,
    borderColor: Colors.light.hereworksBlue500,
    paddingHorizontal: 24,
    alignSelf: 'flex-start',
    marginTop: 20,
  },
  filterButtonTitle: {
    fontSize: 14,
    lineHeight: 22,
    marginVertical: 5,
    fontFamily: 'Roboto_400Regular',
    color: Colors.light.hereworksBlue500,
    marginRight: 8,
  },
  locationDropdownSection: {
    justifyContent: 'flex-start',
  },
  resetAllFiltersButton: {
    justifyContent: 'center',
    marginTop: 20,
    alignSelf: 'flex-start',
    borderBottomWidth: 1,
    borderColor: Colors.light.hereworksPrimaryGrey,
  },
  resetAllFiltersText: {
    marginTop: 6,
    color: Colors.light.hereworksPrimaryGrey,
    fontSize: 12,
    fontFamily: 'Roboto_500Medium',
  },
  assetFilterBottomSection: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 24,
  },
  applyButton: {
    marginStart: 0,
    flex: undefined,
  },
  cancelButton: {
    marginStart: 12,
    marginEnd: 18,
    flex: undefined,
  },
  containerMobile: {
    backgroundColor: '#fff',
    alignSelf: 'flex-end',
    flex: 2,
    borderLeftWidth: 1,
    borderColor: Colors.light.hereworksBlue100,
  },
});

export default AssetFilter;
